'use client';
import { usePortalContainer } from 'v5/primitives/PortalContainer';

import {
  closestCenter,
  DndContext,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  type SortingStrategy
} from '@dnd-kit/sortable';
import { rd } from '@passionware/monads';
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { Columns3, GripVertical, Lock, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  InlineCombobox,
  ComboboxList,
  ComboboxQueryRenderer,
  ComboboxSeparator,
  ComboboxTrigger
} from 'v5/design-system/Combobox';
import { CommandItem } from 'v5/design-system/Command';
import { Switch } from 'v5/design-system/Switch';
import { areEqualKeys as areEqualIds } from 'v5/patterns/dnd/collectionReorder';
import { cn } from 'v5/platform/dom/cn';
import { useDismissableLayerFixFromRef } from 'v5/platform/react/useDismissableLayerFixFromRef';
import { CollectionDropIndicatorFloatingLine } from 'v5/patterns/dnd/CollectionDropIndicator';
import {
  reorderValueMiddle,
  sanitizeTableColumnSelection,
  setFieldPresent,
  type TableColumnManagerField,
  type TableColumnManagerFieldId
} from 'v5/patterns/table/tableColumnManagerSelection';

export type { TableColumnManagerField, TableColumnManagerFieldId };
export { sanitizeTableColumnSelection };

interface ColumnListView {
  presentSet: Set<string>;
  pinnedStart: TableColumnManagerField[];
  reorderable: TableColumnManagerField[];
  pinnedEnd: TableColumnManagerField[];
  available: TableColumnManagerField[];
  hasShownResults: boolean;
  hasAvailableResults: boolean;
  availableCount: number;
}

function buildColumnListView(
  fields: TableColumnManagerField[],
  selection: string[],
  query: string
): ColumnListView {
  const lowerQuery = query.trim().toLowerCase();
  const searching = lowerQuery.length > 0;
  const matchesQuery = (field: TableColumnManagerField) =>
    field.label.toLowerCase().includes(lowerQuery);

  const presentSet = new Set(selection);
  const shown = fields.filter(field => presentSet.has(field.id));
  const availableAll = fields.filter(field => !presentSet.has(field.id));

  const filteredShown = searching ? shown.filter(matchesQuery) : shown;
  const filteredAvailable = searching
    ? availableAll.filter(matchesQuery)
    : availableAll;

  const pinnedStart = filteredShown.filter(field => field.pinned === 'start');
  const pinnedEnd = filteredShown.filter(field => field.pinned === 'end');
  const reorderableById = new Map(
    filteredShown.filter(field => !field.pinned).map(field => [field.id, field])
  );
  const reorderable = selection
    .filter(id => reorderableById.has(id))
    .map(id => reorderableById.get(id)!);

  return {
    presentSet,
    pinnedStart,
    reorderable,
    pinnedEnd,
    available: filteredAvailable,
    hasShownResults: filteredShown.length > 0,
    hasAvailableResults: filteredAvailable.length > 0,
    availableCount: availableAll.length
  };
}

function buildFlatSelectableFieldIds(listView: ColumnListView): string[] {
  return [
    ...listView.pinnedStart.map(field => field.id),
    ...listView.reorderable.map(field => field.id),
    ...listView.pinnedEnd.map(field => field.id),
    ...listView.available.map(field => field.id)
  ];
}

type TableColumnToggleSource = 'keyboard' | 'pointer';

type ToggleFieldHandler = (
  field: TableColumnManagerField,
  checked: boolean,
  source: TableColumnToggleSource
) => void;

/**
 * Which item should hold cmdk focus after a toggle. Keyboard toggles prefer
 * the item that followed the toggled one; otherwise stay at the same index.
 */
function resolveFocusAfterToggle(
  fields: TableColumnManagerField[],
  selectionBefore: string[],
  nextSelection: string[],
  query: string,
  toggledFieldId: string,
  source: TableColumnToggleSource
): string | null {
  const idsBefore = buildFlatSelectableFieldIds(
    buildColumnListView(fields, selectionBefore, query)
  );
  const toggledIndex = idsBefore.indexOf(toggledFieldId);
  if (toggledIndex < 0) {
    return null;
  }

  const idsAfter = buildFlatSelectableFieldIds(
    buildColumnListView(fields, nextSelection, query)
  );
  if (idsAfter.length === 0) {
    return null;
  }

  if (source === 'keyboard') {
    const nextFieldId = idsBefore[toggledIndex + 1];
    if (nextFieldId != null && idsAfter.includes(nextFieldId)) {
      return nextFieldId;
    }
  }

  return idsAfter[Math.min(toggledIndex, idsAfter.length - 1)];
}

function scrollCommandItemIntoView(
  root: HTMLElement | null,
  fieldId: string
): void {
  if (!root) {
    return;
  }
  const item = Array.from(root.querySelectorAll('[cmdk-item]')).find(
    element => element.getAttribute('data-value')?.trim() === fieldId
  );
  item?.scrollIntoView({ block: 'nearest' });
}

export interface TableColumnManagerPopoverLabels {
  title?: string;
  shown?: string;
  available?: string;
  searchPlaceholder?: string;
  reset?: string;
  fixed?: string;
  noShownResults?: string;
  noAvailableResults?: string;
}

export interface TableColumnManagerPopoverProps {
  fields: TableColumnManagerField[];
  value: string[];
  onChange: (next: string[]) => void;
  onReset?: () => void;
  disableReset?: boolean;
  children: ReactNode;
  labels?: TableColumnManagerPopoverLabels;
  contentClassName?: string;
}

export type TableColumnManagerPopoverTriggerProps = ComponentPropsWithRef<
  typeof Button
>;

export const TableColumnManagerPopoverTrigger = forwardRef<
  HTMLButtonElement,
  TableColumnManagerPopoverTriggerProps
>(function TableColumnManagerPopoverTrigger(
  { className, 'aria-label': ariaLabel, ...rest },
  ref
) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'TableColumnManagerPopover'
  });

  return (
    <Button
      ref={ref}
      variant="secondary"
      size="sm"
      layout="icon"
      aria-label={ariaLabel ?? t('triggerAriaLabel')}
      className={className}
      {...rest}
    >
      <Columns3 className="size-5" />
    </Button>
  );
});

TableColumnManagerPopoverTrigger.displayName =
  'TableColumnManagerPopoverTrigger';

function ColumnRowSwitch({
  field,
  checked,
  disabled,
  onCheckedChange
}: {
  field: TableColumnManagerField;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'TableColumnManagerPopover'
  });

  return (
    <Switch
      key={field.id}
      checked={checked}
      disabled={disabled}
      tabIndex={-1}
      className="ml-auto shrink-0"
      onPointerDown={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
      onCheckedChange={next => onCheckedChange(next === true)}
      aria-label={
        checked
          ? t('hideField', { field: field.label })
          : t('showField', { field: field.label })
      }
    />
  );
}

function swapReorderableFields(
  fields: TableColumnManagerField[],
  index: number,
  direction: -1 | 1
): string[] | null {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= fields.length) {
    return null;
  }
  const next = [...fields];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next.map(field => field.id);
}

type ColumnManagerDropIndicator = {
  overId: string;
  edge: 'top' | 'bottom';
};

type ColumnManagerDropLineRect = {
  top: number;
  left: number;
  width: number;
};

type ColumnManagerOverlayRect = ColumnManagerDropLineRect & {
  height: number;
};

function getActiveOverlayRect(
  active: DragMoveEvent['active'],
  delta?: { x: number; y: number }
): ColumnManagerOverlayRect | null {
  const initial = active.rect.current.initial;
  if (!initial) {
    return null;
  }

  const translated = active.rect.current.translated;
  const top = translated?.top ?? initial.top + (delta?.y ?? 0);

  return {
    top,
    left: initial.left,
    width: initial.width,
    height: initial.height
  };
}

function ColumnManagerDragLayer({
  overlayRect,
  overlayField,
  overlayChecked,
  dropLineRect
}: {
  overlayRect: ColumnManagerOverlayRect | null;
  overlayField?: TableColumnManagerField;
  overlayChecked: boolean;
  dropLineRect: ColumnManagerDropLineRect | null;
}) {
  const portalContainer = usePortalContainer();
  return (
    <>
      <CollectionDropIndicatorFloatingLine rect={dropLineRect} />
      {overlayRect && overlayField
        ? createPortal(
            <div
              aria-hidden
              className="pointer-events-none fixed z-10001"
              style={{
                top: overlayRect.top,
                left: overlayRect.left,
                width: overlayRect.width,
                height: overlayRect.height
              }}
            >
              <ColumnManagerDragOverlayRow
                field={overlayField}
                checked={overlayChecked}
                width={overlayRect.width}
              />
            </div>,
            portalContainer
          )
        : null}
    </>
  );
}

/** Keeps list layout fixed during drag — only overlay and drop line move until drop. */
const columnManagerSortableStrategy: SortingStrategy = () => null;

/** Active item stays under the pointer; exclude it so we target insertion rows. */
const columnManagerCollisionDetection: CollisionDetection = args =>
  closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      container => container.id !== args.active.id
    )
  });

function computeColumnManagerDropIndicator(
  active: DragMoveEvent['active'],
  over: DragMoveEvent['over'] | null,
  delta?: { x: number; y: number }
): ColumnManagerDropIndicator | null {
  if (!over || String(active.id) === String(over.id)) {
    return null;
  }

  const overlayRect = getActiveOverlayRect(active, delta);
  if (!overlayRect) {
    return { overId: String(over.id), edge: 'bottom' };
  }

  const dragCenterY = overlayRect.top + overlayRect.height / 2;
  const overRect = over.rect;
  const edge =
    dragCenterY < overRect.top + overRect.height / 2 ? 'top' : 'bottom';

  return { overId: String(over.id), edge };
}

function computeColumnManagerDropLineRect(
  over: DragMoveEvent['over'],
  edge: 'top' | 'bottom',
  anchor?: Pick<ColumnManagerDropLineRect, 'left' | 'width'> | null
): ColumnManagerDropLineRect | null {
  if (!over) {
    return null;
  }

  const { top, left, width, height } = over.rect;
  return {
    top: edge === 'top' ? top : top + height,
    left: anchor?.left ?? left,
    width: anchor?.width ?? width
  };
}

function reorderFieldsAtDropIndicator(
  fields: TableColumnManagerField[],
  activeId: string,
  indicator: ColumnManagerDropIndicator
): string[] | null {
  const oldIndex = fields.findIndex(field => field.id === activeId);
  const overIndex = fields.findIndex(field => field.id === indicator.overId);
  if (oldIndex === -1 || overIndex === -1) {
    return null;
  }

  let newIndex = indicator.edge === 'top' ? overIndex : overIndex + 1;
  if (oldIndex < newIndex) {
    newIndex -= 1;
  }
  if (oldIndex === newIndex) {
    return null;
  }

  return arrayMove(fields, oldIndex, newIndex).map(field => field.id);
}

/** Fallback when the drop indicator is unavailable: move active onto the over item's index. */
function reorderFieldsToOverIndex(
  fields: TableColumnManagerField[],
  activeId: string,
  overId: string
): string[] | null {
  const oldIndex = fields.findIndex(field => field.id === activeId);
  const newIndex = fields.findIndex(field => field.id === overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return null;
  }
  return arrayMove(fields, oldIndex, newIndex).map(field => field.id);
}

function isReorderKeyboardEvent(event: {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  key: string;
}) {
  return (
    event.shiftKey &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey &&
    (event.key === 'ArrowUp' || event.key === 'ArrowDown')
  );
}

function resolveActiveCommandItemId(
  root: HTMLElement | null
): string | undefined {
  const item = resolveActiveCommandItem(root);
  const value = item?.getAttribute('data-value')?.trim();
  return value || undefined;
}

const CMDK_ITEM_SELECT_EVENT = 'cmdk-item-select';

function resolveActiveCommandItem(
  root: HTMLElement | null,
  selectedId?: string
): HTMLElement | null {
  if (!root) {
    return null;
  }

  const bySelection = (root.querySelector(
    '[cmdk-item][aria-selected="true"]'
  ) ??
    root.querySelector(
      '[cmdk-item][data-selected="true"]'
    )) as HTMLElement | null;
  if (bySelection) {
    return bySelection;
  }

  const id = selectedId?.trim();
  if (!id) {
    return null;
  }

  const items = root.querySelectorAll('[cmdk-item]');
  for (const element of items) {
    if (element.getAttribute('data-value')?.trim() === id) {
      return element as HTMLElement;
    }
  }
  return null;
}

function triggerCommandItemSelect(item: HTMLElement): void {
  item.dispatchEvent(new Event(CMDK_ITEM_SELECT_EVENT, { bubbles: false }));
}

/** Last-focused shown middle field, if it's still a valid insertion anchor. */
function resolveInsertAfterFieldId(
  fields: TableColumnManagerField[],
  selection: string[],
  lastShownMiddleFocusId: string | null,
  toggledFieldId: string
): string | null {
  const id = lastShownMiddleFocusId;
  if (!id || id === toggledFieldId || !selection.includes(id)) {
    return null;
  }
  const field = fields.find(candidate => candidate.id === id);
  return field != null && !field.pinned ? id : null;
}

function ColumnRow({
  field,
  checked,
  leadingSlot,
  onToggleField,
  onShownFieldFocus,
  highlightClassName,
  keyboardSelectPendingRef
}: {
  field: TableColumnManagerField;
  checked: boolean;
  leadingSlot: ReactNode;
  onToggleField: ToggleFieldHandler;
  onShownFieldFocus?: (fieldId: string) => void;
  highlightClassName?: string;
  keyboardSelectPendingRef: MutableRefObject<boolean>;
}) {
  const disabled = !!field.mandatory || !!field.pinned;
  const suppressRowSelectRef = useRef(false);

  const handleRowKeyDown = (event: ReactKeyboardEvent) => {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      keyboardSelectPendingRef.current = true;
    }
  };

  const handleSwitchCheckedChange = (nextChecked: boolean) => {
    suppressRowSelectRef.current = true;
    onToggleField(field, nextChecked, 'pointer');
    requestAnimationFrame(() => {
      suppressRowSelectRef.current = false;
    });
  };

  return (
    <CommandItem
      value={field.id}
      keywords={[field.label]}
      disabled={disabled}
      onKeyDown={handleRowKeyDown}
      onFocus={() => {
        if (!field.pinned && onShownFieldFocus) {
          onShownFieldFocus(field.id);
        }
      }}
      className={highlightClassName}
      onSelect={() => {
        if (disabled) {
          return;
        }
        if (suppressRowSelectRef.current) {
          return;
        }
        const source: TableColumnToggleSource = keyboardSelectPendingRef.current
          ? 'keyboard'
          : 'pointer';
        keyboardSelectPendingRef.current = false;
        onToggleField(field, !checked, source);
      }}
    >
      {leadingSlot}
      {field.icon}
      <span className="flex-1 min-w-0 truncate">{field.label}</span>
      <ColumnRowSwitch
        field={field}
        checked={checked}
        disabled={disabled}
        onCheckedChange={handleSwitchCheckedChange}
      />
    </CommandItem>
  );
}

function PinnedColumnRow({
  field,
  checked,
  fixedLabel,
  onToggleField,
  highlightClassName,
  keyboardSelectPendingRef
}: {
  field: TableColumnManagerField;
  checked: boolean;
  fixedLabel: string;
  onToggleField: ToggleFieldHandler;
  highlightClassName?: string;
  keyboardSelectPendingRef: MutableRefObject<boolean>;
}) {
  return (
    <ColumnRow
      field={field}
      checked={checked}
      onToggleField={onToggleField}
      highlightClassName={highlightClassName}
      keyboardSelectPendingRef={keyboardSelectPendingRef}
      leadingSlot={<Lock aria-label={fixedLabel} />}
    />
  );
}

function AvailableColumnRow({
  field,
  onToggleField,
  keyboardSelectPendingRef
}: {
  field: TableColumnManagerField;
  onToggleField: ToggleFieldHandler;
  keyboardSelectPendingRef: MutableRefObject<boolean>;
}) {
  return (
    <ColumnRow
      field={field}
      checked={false}
      onToggleField={onToggleField}
      keyboardSelectPendingRef={keyboardSelectPendingRef}
      leadingSlot={<span className="size-5 shrink-0" aria-hidden />}
    />
  );
}

function ColumnManagerDragOverlayRow({
  field,
  checked,
  width
}: {
  field: TableColumnManagerField;
  checked: boolean;
  width?: number;
}) {
  return (
    <div
      className="flex cursor-grabbing select-none items-center gap-2 rounded-sm bg-bg-subtle/90 px-2 py-1.5 text-base shadow-md [&_svg]:size-5 [&_svg]:shrink-0"
      style={width != null ? { width, boxSizing: 'border-box' } : undefined}
    >
      <GripVertical className="text-fg-muted" />
      {field.icon}
      <span className="flex-1 min-w-0 truncate">{field.label}</span>
      <Switch
        key={field.id}
        checked={checked}
        disabled
        tabIndex={-1}
        className="ml-auto shrink-0"
        aria-hidden
      />
    </div>
  );
}

function ReorderableColumnRow({
  field,
  checked,
  searching,
  dragHandleProps,
  onToggleField,
  onShownFieldFocus,
  highlightClassName,
  keyboardSelectPendingRef
}: {
  field: TableColumnManagerField;
  checked: boolean;
  searching: boolean;
  dragHandleProps?: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
    setActivatorNodeRef: ReturnType<typeof useSortable>['setActivatorNodeRef'];
  };
  onToggleField: ToggleFieldHandler;
  onShownFieldFocus?: (fieldId: string) => void;
  highlightClassName?: string;
  keyboardSelectPendingRef: MutableRefObject<boolean>;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'TableColumnManagerPopover'
  });

  return (
    <ColumnRow
      field={field}
      checked={checked}
      onToggleField={onToggleField}
      onShownFieldFocus={onShownFieldFocus}
      highlightClassName={highlightClassName}
      keyboardSelectPendingRef={keyboardSelectPendingRef}
      leadingSlot={
        <button
          type="button"
          ref={dragHandleProps?.setActivatorNodeRef}
          className={cn(
            'size-9 -m-1.5 -mr-2.5 flex items-center justify-center  shrink-0 min-h-0 min-w-0 p-0',
            searching
              ? 'cursor-default opacity-50'
              : 'cursor-grab active:cursor-grabbing touch-none select-none'
          )}
          aria-disabled={searching || undefined}
          aria-label={t('dragHandle', { field: field.label })}
          {...dragHandleProps?.attributes}
          {...dragHandleProps?.listeners}
          tabIndex={-1}
        >
          <GripVertical />
        </button>
      }
    />
  );
}

function SortableColumnRow({
  field,
  checked,
  onToggleField,
  onShownFieldFocus,
  highlightClassName,
  keyboardSelectPendingRef
}: {
  field: TableColumnManagerField;
  checked: boolean;
  onToggleField: ToggleFieldHandler;
  onShownFieldFocus?: (fieldId: string) => void;
  highlightClassName?: string;
  keyboardSelectPendingRef: MutableRefObject<boolean>;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useSortable({
      id: field.id,
      animateLayoutChanges: () => false,
      transition: null
    });

  return (
    <div
      ref={setNodeRef}
      className={cn('relative min-w-0', isDragging && 'opacity-40')}
    >
      <ReorderableColumnRow
        field={field}
        checked={checked}
        searching={false}
        dragHandleProps={{ attributes, listeners, setActivatorNodeRef }}
        onToggleField={onToggleField}
        onShownFieldFocus={onShownFieldFocus}
        highlightClassName={highlightClassName}
        keyboardSelectPendingRef={keyboardSelectPendingRef}
      />
    </div>
  );
}

function ReorderableColumnRows({
  fields,
  presentSet,
  searching,
  onToggleField,
  onReorder,
  onShownFieldFocus,
  getRowHighlightClassName,
  keyboardSelectPendingRef,
  scrollContainerRef
}: {
  fields: TableColumnManagerField[];
  presentSet: Set<string>;
  searching: boolean;
  onToggleField: ToggleFieldHandler;
  onReorder: (reorderedMiddleIds: string[]) => void;
  onShownFieldFocus?: (fieldId: string) => void;
  getRowHighlightClassName: (fieldId: string) => string | undefined;
  keyboardSelectPendingRef: MutableRefObject<boolean>;
  scrollContainerRef?: MutableRefObject<HTMLElement | null>;
}) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [overlayRect, setOverlayRect] =
    useState<ColumnManagerOverlayRect | null>(null);
  const [dropIndicator, setDropIndicator] =
    useState<ColumnManagerDropIndicator | null>(null);
  const [dropLineRect, setDropLineRect] =
    useState<ColumnManagerDropLineRect | null>(null);
  const dropIndicatorRef = useRef<ColumnManagerDropIndicator | null>(null);
  const dropLineAnchorRef = useRef<Pick<
    ColumnManagerDropLineRect,
    'left' | 'width'
  > | null>(null);
  dropIndicatorRef.current = dropIndicator;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 }
    })
  );

  useEffect(() => {
    if (!activeDragId) {
      return;
    }

    const el = scrollContainerRef?.current;
    if (!el) {
      return;
    }

    let frame = 0;
    const pinHorizontalScroll = () => {
      if (el.scrollLeft !== 0) {
        el.scrollLeft = 0;
      }
      frame = requestAnimationFrame(pinHorizontalScroll);
    };

    pinHorizontalScroll();
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [activeDragId, scrollContainerRef]);

  const clearDragUi = useCallback(() => {
    setActiveDragId(null);
    setOverlayRect(null);
    setDropIndicator(null);
    setDropLineRect(null);
    dropLineAnchorRef.current = null;
  }, []);

  const syncDragUi = useCallback((event: DragMoveEvent) => {
    setOverlayRect(getActiveOverlayRect(event.active, event.delta));

    const indicator = computeColumnManagerDropIndicator(
      event.active,
      event.over,
      event.delta
    );
    setDropIndicator(indicator);
    if (!indicator || !event.over) {
      setDropLineRect(null);
      return;
    }
    setDropLineRect(
      computeColumnManagerDropLineRect(
        event.over,
        indicator.edge,
        dropLineAnchorRef.current
      )
    );
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const initial = event.active.rect.current.initial;
    dropLineAnchorRef.current = initial
      ? { left: initial.left, width: initial.width }
      : null;
    setActiveDragId(String(event.active.id));
    setDropIndicator(null);
    setDropLineRect(null);
    setOverlayRect(getActiveOverlayRect(event.active));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const indicator = dropIndicatorRef.current;
    clearDragUi();

    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const nextOrder = indicator
      ? reorderFieldsAtDropIndicator(fields, String(active.id), indicator)
      : reorderFieldsToOverIndex(fields, String(active.id), String(over.id));

    if (nextOrder) {
      onReorder(nextOrder);
    }
  };

  if (searching) {
    return (
      <>
        {fields.map(field => (
          <ReorderableColumnRow
            key={field.id}
            field={field}
            checked={presentSet.has(field.id)}
            searching
            onToggleField={onToggleField}
            onShownFieldFocus={onShownFieldFocus}
            highlightClassName={getRowHighlightClassName(field.id)}
            keyboardSelectPendingRef={keyboardSelectPendingRef}
          />
        ))}
      </>
    );
  }

  const activeDragField = activeDragId
    ? fields.find(field => field.id === activeDragId)
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={columnManagerCollisionDetection}
      autoScroll={{
        threshold: {
          x: 0,
          y: 0.2
        },
        layoutShiftCompensation: false
      }}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.BeforeDragging
        }
      }}
      onDragStart={handleDragStart}
      onDragMove={syncDragUi}
      onDragOver={syncDragUi}
      onDragCancel={clearDragUi}
      onDragEnd={handleDragEnd}
    >
      <ColumnManagerDragLayer
        overlayRect={overlayRect}
        overlayField={activeDragField}
        overlayChecked={
          activeDragField ? presentSet.has(activeDragField.id) : false
        }
        dropLineRect={dropLineRect}
      />
      <SortableContext
        items={fields.map(field => field.id)}
        strategy={columnManagerSortableStrategy}
      >
        {fields.map(field => (
          <SortableColumnRow
            key={field.id}
            field={field}
            checked={presentSet.has(field.id)}
            onToggleField={onToggleField}
            onShownFieldFocus={onShownFieldFocus}
            highlightClassName={getRowHighlightClassName(field.id)}
            keyboardSelectPendingRef={keyboardSelectPendingRef}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function isColumnManagerSearchKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.matches('input, textarea, [contenteditable="true"]') ||
    target.closest('[cmdk-input-wrapper]') != null
  );
}

function ColumnManagerResetItem({
  label,
  disabled,
  onReset
}: {
  label: string;
  disabled: boolean;
  onReset: () => void;
}) {
  return (
    <div className="shrink-0 border-t border-border-default bg-bg-default p-1">
      <CommandItem
        value="__column-manager-reset__"
        disabled={disabled}
        onSelect={() => {
          if (!disabled) {
            onReset();
          }
        }}
        className="gap-2 rounded-none bg-bg-default data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-100 data-[disabled=true]:text-fg-muted data-[disabled=true]:[&_svg]:text-fg-muted"
      >
        <RotateCcw />
        {label}
      </CommandItem>
    </div>
  );
}

function ColumnManagerList({
  listView,
  copy,
  searching,
  onToggleField,
  onReorder,
  onShownFieldFocus,
  getRowHighlightClassName,
  keyboardSelectPendingRef,
  scrollContainerRef
}: {
  listView: ColumnListView;
  copy: Required<TableColumnManagerPopoverLabels>;
  searching: boolean;
  onToggleField: ToggleFieldHandler;
  onReorder: (reorderedMiddleIds: string[]) => void;
  onShownFieldFocus?: (fieldId: string) => void;
  getRowHighlightClassName: (fieldId: string) => string | undefined;
  keyboardSelectPendingRef: MutableRefObject<boolean>;
  scrollContainerRef?: MutableRefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'TableColumnManagerPopover'
  });

  const availableHeading = `${copy.available} · ${t('availableFieldsCount', {
    count: listView.availableCount
  })}`;

  return (
    <>
      {listView.hasShownResults ? (
        <ComboboxGroup heading={copy.shown}>
          {listView.pinnedStart.map(field => (
            <PinnedColumnRow
              key={field.id}
              field={field}
              checked={listView.presentSet.has(field.id)}
              fixedLabel={copy.fixed}
              onToggleField={onToggleField}
              highlightClassName={getRowHighlightClassName(field.id)}
              keyboardSelectPendingRef={keyboardSelectPendingRef}
            />
          ))}
          <ReorderableColumnRows
            fields={listView.reorderable}
            presentSet={listView.presentSet}
            searching={searching}
            onToggleField={onToggleField}
            onReorder={onReorder}
            onShownFieldFocus={onShownFieldFocus}
            getRowHighlightClassName={getRowHighlightClassName}
            keyboardSelectPendingRef={keyboardSelectPendingRef}
            scrollContainerRef={scrollContainerRef}
          />
          {listView.pinnedEnd.map(field => (
            <PinnedColumnRow
              key={field.id}
              field={field}
              checked={listView.presentSet.has(field.id)}
              fixedLabel={copy.fixed}
              onToggleField={onToggleField}
              highlightClassName={getRowHighlightClassName(field.id)}
              keyboardSelectPendingRef={keyboardSelectPendingRef}
            />
          ))}
        </ComboboxGroup>
      ) : null}

      {listView.hasShownResults && listView.hasAvailableResults ? (
        <ComboboxSeparator />
      ) : null}

      {listView.hasAvailableResults ? (
        <ComboboxGroup heading={availableHeading}>
          {listView.available.map(field => (
            <AvailableColumnRow
              key={field.id}
              field={field}
              onToggleField={onToggleField}
              keyboardSelectPendingRef={keyboardSelectPendingRef}
            />
          ))}
        </ComboboxGroup>
      ) : null}
    </>
  );
}

function useColumnManagerCopy(
  labels: TableColumnManagerPopoverLabels | undefined
): Required<TableColumnManagerPopoverLabels> {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'TableColumnManagerPopover'
  });
  return useMemo(
    () => ({
      title: labels?.title ?? t('title'),
      shown: labels?.shown ?? t('shown'),
      available: labels?.available ?? t('available'),
      searchPlaceholder: labels?.searchPlaceholder ?? t('searchPlaceholder'),
      reset: labels?.reset ?? t('reset'),
      fixed: labels?.fixed ?? t('fixed'),
      noShownResults: labels?.noShownResults ?? t('noShownResults'),
      noAvailableResults: labels?.noAvailableResults ?? t('noAvailableResults')
    }),
    [labels, t]
  );
}

/**
 * Selection, highlight and keyboard handling of a column manager. `active` is while its list is
 * on screen (the popover open, or always for the inline list); `rootRef` holds the list's root.
 */
function useColumnManagerController({
  fields,
  value,
  onChange,
  active,
  rootRef
}: {
  fields: TableColumnManagerField[];
  value: string[];
  onChange: (next: string[]) => void;
  active: boolean;
  rootRef: MutableRefObject<HTMLElement | null>;
}) {
  const [commandValue, setCommandValue] = useState('');
  const commandValueRef = useRef(commandValue);
  commandValueRef.current = commandValue;
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(
    null
  );
  const listScrollRef = useRef<HTMLDivElement>(null);
  const keyboardSelectPendingRef = useRef(false);
  const pendingScrollFieldIdRef = useRef<{
    fieldId: string;
    selectionBefore: string[];
  } | null>(null);
  const queryRef = useRef('');
  const lastShownMiddleFocusRef = useRef<string | null>(null);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const selection = useMemo(
    () => sanitizeTableColumnSelection(fields, value),
    [fields, value]
  );
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  const commit = useCallback(
    (next: string[]) => {
      const sanitized = sanitizeTableColumnSelection(fieldsRef.current, next);
      if (!areEqualIds(sanitized, selectionRef.current)) {
        onChange(sanitized);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (active) {
      queryRef.current = '';
      return;
    }
    setHighlightedFieldId(null);
    setCommandValue('');
    pendingScrollFieldIdRef.current = null;
    keyboardSelectPendingRef.current = false;
  }, [active]);

  useEffect(() => {
    if (!highlightedFieldId) {
      return;
    }
    const clearTimer = window.setTimeout(
      () => setHighlightedFieldId(null),
      900
    );
    return () => {
      window.clearTimeout(clearTimer);
    };
  }, [highlightedFieldId]);

  useLayoutEffect(() => {
    const pending = pendingScrollFieldIdRef.current;
    if (!pending) {
      return;
    }
    if (areEqualIds(pending.selectionBefore, selection)) {
      return;
    }
    pendingScrollFieldIdRef.current = null;
    scrollCommandItemIntoView(rootRef.current, pending.fieldId);
  }, [selection]);

  const getRowHighlightClassName = useCallback(
    (fieldId: string) => {
      if (highlightedFieldId !== fieldId) {
        return undefined;
      }
      return 'animate-column-manager-row-flash';
    },
    [highlightedFieldId]
  );

  const handleShownFieldFocus = useCallback((fieldId: string) => {
    lastShownMiddleFocusRef.current = fieldId;
  }, []);

  const toggleField = (
    field: TableColumnManagerField,
    checked: boolean,
    source: TableColumnToggleSource
  ) => {
    if (field.mandatory || field.pinned) {
      return;
    }
    const selectionBefore = selectionRef.current;
    const adding = checked && !selectionBefore.includes(field.id);
    const insertAfterFieldId = adding
      ? resolveInsertAfterFieldId(
          fieldsRef.current,
          selectionBefore,
          lastShownMiddleFocusRef.current,
          field.id
        )
      : null;
    const nextSelection = setFieldPresent(
      fieldsRef.current,
      selectionBefore,
      field.id,
      checked,
      insertAfterFieldId
    );
    const focusId = resolveFocusAfterToggle(
      fieldsRef.current,
      selectionBefore,
      nextSelection,
      queryRef.current,
      field.id,
      source
    );
    if (focusId != null) {
      setCommandValue(focusId);
      pendingScrollFieldIdRef.current = { fieldId: focusId, selectionBefore };
    }
    if (adding) {
      setHighlightedFieldId(field.id);
    }
    commit(nextSelection);
  };

  const handleReorder = useCallback(
    (reorderedMiddleIds: string[]) => {
      commit(
        reorderValueMiddle(
          fieldsRef.current,
          selectionRef.current,
          reorderedMiddleIds
        )
      );
    },
    [commit]
  );

  const tryReorderFromKeyboard = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (!active || !isReorderKeyboardEvent(event)) {
        return false;
      }
      if (queryRef.current.trim().length > 0) {
        return false;
      }
      const root = rootRef.current;
      if (
        !root ||
        !(event.target instanceof Node) ||
        !root.contains(event.target)
      ) {
        return false;
      }
      const selectedId =
        commandValueRef.current.trim() || resolveActiveCommandItemId(root);
      if (!selectedId) {
        return false;
      }
      const listView = buildColumnListView(
        fieldsRef.current,
        selectionRef.current,
        queryRef.current
      );
      const index = listView.reorderable.findIndex(
        field => field.id === selectedId
      );
      if (index === -1) {
        return false;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const direction = event.key === 'ArrowUp' ? -1 : 1;
      const nextOrder = swapReorderableFields(
        listView.reorderable,
        index,
        direction
      );
      if (nextOrder) {
        handleReorder(nextOrder);
      }
      return true;
    },
    [handleReorder, active]
  );

  useEffect(() => {
    if (!active) {
      return;
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (tryReorderFromKeyboard(event)) {
        return;
      }
      const root = rootRef.current;
      if (
        !root ||
        !(event.target instanceof Node) ||
        !root.contains(event.target)
      ) {
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      if (event.key === ' ' && isColumnManagerSearchKeyTarget(event.target)) {
        return;
      }
      const selectedId =
        commandValueRef.current.trim() || resolveActiveCommandItemId(root);
      const item = resolveActiveCommandItem(root, selectedId);
      if (!item || item.getAttribute('aria-disabled') === 'true') {
        return;
      }
      keyboardSelectPendingRef.current = true;
      if (event.key === ' ') {
        event.preventDefault();
        event.stopImmediatePropagation();
        triggerCommandItemSelect(item);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [active, tryReorderFromKeyboard]);

  return {
    commandValue,
    setCommandValue,
    listScrollRef,
    queryRef,
    fieldsRef,
    selectionRef,
    keyboardSelectPendingRef,
    toggleField,
    handleReorder,
    handleShownFieldFocus,
    getRowHighlightClassName
  };
}

function ColumnManagerBody({
  controller,
  copy,
  onReset,
  disableReset,
  scrollClassName,
  focusSearchOnOpen
}: {
  controller: ReturnType<typeof useColumnManagerController>;
  copy: Required<TableColumnManagerPopoverLabels>;
  onReset?: () => void;
  disableReset: boolean;
  scrollClassName?: string;
  focusSearchOnOpen: boolean;
}) {
  return (
    <>
      <ComboboxInput
        placeholder={copy.searchPlaceholder}
        focusOnOpen={focusSearchOnOpen}
      />

      <ComboboxList className="flex flex-col overflow-x-hidden overflow-y-hidden p-0">
        <div
          ref={controller.listScrollRef}
          className={cn(
            'overflow-x-hidden overflow-y-auto overscroll-x-none scrollbar-gutter-stable with-styled-scrollbars',
            scrollClassName
          )}
        >
          <ComboboxQueryRenderer emptyContent={copy.noShownResults}>
            {query => {
              controller.queryRef.current = query;
              const searching = query.trim().length > 0;
              const listView = buildColumnListView(
                controller.fieldsRef.current,
                controller.selectionRef.current,
                query
              );

              if (!listView.hasShownResults && !listView.hasAvailableResults) {
                return rd.of(null);
              }

              return rd.of(
                <ColumnManagerList
                  listView={listView}
                  copy={copy}
                  searching={searching}
                  onToggleField={controller.toggleField}
                  onReorder={controller.handleReorder}
                  onShownFieldFocus={controller.handleShownFieldFocus}
                  getRowHighlightClassName={controller.getRowHighlightClassName}
                  keyboardSelectPendingRef={controller.keyboardSelectPendingRef}
                  scrollContainerRef={controller.listScrollRef}
                />
              );
            }}
          </ComboboxQueryRenderer>
        </div>

        {onReset != null ? (
          <ColumnManagerResetItem
            label={copy.reset}
            disabled={disableReset}
            onReset={onReset}
          />
        ) : null}
      </ComboboxList>
    </>
  );
}

export function TableColumnManagerPopover({
  fields,
  value,
  onChange,
  onReset,
  disableReset = false,
  children,
  labels,
  contentClassName
}: TableColumnManagerPopoverProps) {
  const copy = useColumnManagerCopy(labels);
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const controller = useColumnManagerController({
    fields,
    value,
    onChange,
    active: open,
    rootRef: contentRef
  });

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useDismissableLayerFixFromRef(open, handleClose, contentRef);

  return (
    <Combobox open={open} onOpenChange={setOpen} defaultQuery="">
      <ComboboxTrigger asChild>{children}</ComboboxTrigger>
      <ComboboxContent
        ref={contentRef}
        sideOffset={8}
        align="start"
        shouldFilter={false}
        value={controller.commandValue}
        onValueChange={controller.setCommandValue}
        className={cn(
          'flex min-h-0 w-[300px] flex-col overflow-x-hidden overflow-y-hidden',
          contentClassName
        )}
      >
        <div className="border-b border-border-default px-3 py-2.5 font-semibold">
          {copy.title}
        </div>

        <ColumnManagerBody
          controller={controller}
          copy={copy}
          onReset={onReset}
          disableReset={disableReset}
          scrollClassName="max-h-[calc(460px-3rem)]"
          focusSearchOnOpen
        />
      </ComboboxContent>
    </Combobox>
  );
}

export type TableColumnManagerListProps = Omit<
  TableColumnManagerPopoverProps,
  'children' | 'contentClassName'
> & { className?: string };

/** The column manager's search and lists on the page itself, e.g. as a form field. */
export function TableColumnManagerList({
  fields,
  value,
  onChange,
  onReset,
  disableReset = false,
  labels,
  className
}: TableColumnManagerListProps) {
  const copy = useColumnManagerCopy(labels);
  const rootRef = useRef<HTMLDivElement>(null);
  const controller = useColumnManagerController({
    fields,
    value,
    onChange,
    active: true,
    rootRef
  });

  return (
    <div ref={rootRef} className={className}>
      <InlineCombobox
        value={controller.commandValue}
        onValueChange={controller.setCommandValue}
      >
        <ColumnManagerBody
          controller={controller}
          copy={copy}
          onReset={onReset}
          disableReset={disableReset}
          focusSearchOnOpen={false}
        />
      </InlineCombobox>
    </div>
  );
}
